import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import ProductCatalogNav from '@/components/products/ProductCatalogNav'
import SaveBar from '@/components/forms/SaveBar'
import {
  IndexDataTable,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ToggleButton } from '@/components/ui/toggle-button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { useAppForm } from '@/lib/form'
import {
  inventoryQueries,
  useInventory,
  useUpdateInventoryMutation,
} from '@/lib/services/inventory/inventory.query'
import {
  buildInventoryUpdatePayload,
  getVisibleInventoryProduct,
  getProductTrackAllState,
  getTrackedProductOnHandTotal,
  getTrackedVariantCount,
  inventoryProductMatchesType,
  isPendingUntrackedVariant,
  isTrackedProductOutOfStock,
  isTrackedVariantOutOfStock,
  normalizeInventoryFieldErrors,
  normalizeInventoryFormValues,
  sanitizeInventoryQuantityInput,
} from '@/lib/services/inventory/inventory.utils'
import { getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { createFileRoute, Link, notFound, useParams } from '@tanstack/react-router'
import {
  Boxes,
  ChevronRight,
  Eye,
  EyeOff,
  Flower2,
  MapPin,
  PackagePlus,
  SlidersHorizontal,
  TriangleAlert,
} from 'lucide-react'
import {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

export function shouldPrefetchInventory(orgId) {
  return Number(orgId) !== Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)
}

const defaultInventoryProductType = 'all'

const inventoryProductTypeFilterOptions = [
  { value: 'all', label: 'All', icon: <SlidersHorizontal className="size-4" /> },
  { value: 'products', label: 'Products', icon: <Flower2 className="size-4" /> },
  { value: 'add-ons', label: 'Add-ons', icon: <PackagePlus className="size-4" /> },
]

export const Route = createFileRoute('/_authenticated/$orgId/products/inventory')({
  component: InventoryRouteComponent,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'products')) throw notFound()

    if (shouldPrefetchInventory(params.orgId)) {
      await context.queryClient.ensureQueryData(
        inventoryQueries.index({ organizationId: params.orgId })
      )
    }

    return { isReadOnly: !context.ability.can('edit', 'products') }
  },
})

export function InventoryRouteComponent() {
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const [query, setQuery] = useState('')
  const [productType, setProductType] = useState(defaultInventoryProductType)
  const [showUntracked, setShowUntracked] = useState(false)
  const filterQuery = useDeferredValue(query)

  const updateSearch = (nextSearch) => setQuery(nextSearch)
  const updateProductType = (nextProductType) =>
    startTransition(() => setProductType(nextProductType))
  const updateShowUntracked = (checked) => startTransition(() => setShowUntracked(checked))

  return (
    <div className="w-full max-w-none space-y-4">
      <ProductCatalogNav active="inventory" />
      <InventoryPageIntro />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice title="Choose an organization first">
            Inventory is managed inside each florist account.
          </OrganizationContextNotice>
        }
      >
        <InventoryRouteContent
          key={orgId}
          orgId={orgId}
          query={query}
          filterQuery={filterQuery}
          productType={productType}
          showUntracked={showUntracked}
          onSearchChange={updateSearch}
          onProductTypeChange={updateProductType}
          onShowUntrackedChange={updateShowUntracked}
          inventorySetupPath={path.locations?.create?.()}
          productCreatePath={path.products?.create?.()}
        />
      </IfGlobalOrg>
    </div>
  )
}

function InventoryPageIntro() {
  return (
    <AdminPageHeader
      icon={Boxes}
      title="Inventory"
      description="Check what's on hand, update stock counts, and choose what to track in one place."
    />
  )
}

function InventoryRouteContent({
  orgId,
  query,
  filterQuery,
  productType,
  showUntracked,
  onSearchChange,
  onProductTypeChange,
  onShowUntrackedChange,
  inventorySetupPath,
  productCreatePath,
}) {
  const { data: inventoryResponse, isFetching } = useInventory()
  const updateInventoryMutation = useUpdateInventoryMutation()

  return (
    <InventoryManagementForm
      key={orgId}
      inventoryData={inventoryResponse?.data ?? []}
      meta={inventoryResponse?.meta ?? {}}
      mutation={updateInventoryMutation}
      query={query}
      filterQuery={filterQuery}
      productType={productType}
      showUntracked={showUntracked}
      onSearchChange={onSearchChange}
      onProductTypeChange={onProductTypeChange}
      onShowUntrackedChange={onShowUntrackedChange}
      inventorySetupPath={inventorySetupPath}
      productCreatePath={productCreatePath}
      isFetching={isFetching}
    />
  )
}

function buildInventoryProductSlots(products = []) {
  return products.map((product, productIndex) => ({
    id: product.id,
    productIndex,
    image: product.image ?? null,
  }))
}

function buildVisibleInventoryProducts({
  products = [],
  persistedProducts = [],
  productSlots = [],
  query = '',
  productType = defaultInventoryProductType,
  showUntracked = false,
}) {
  return productSlots.reduce((visibleProducts, productSlot) => {
    const product = products[productSlot.productIndex]
    const persistedProduct = persistedProducts?.[productSlot.productIndex] ?? null
    const visibleProduct = getVisibleInventoryProduct(
      product,
      {
        query,
        productType,
        showUntracked,
        defaultProduct: persistedProduct,
      },
      productSlot.productIndex
    )

    if (!visibleProduct) {
      return visibleProducts
    }

    visibleProducts.push({
      productImage: productSlot.image ?? null,
      persistedProduct,
      visibleProduct,
    })

    return visibleProducts
  }, [])
}

function buildInventoryTableRows(visibleInventoryProducts = []) {
  return visibleInventoryProducts.flatMap(
    ({ visibleProduct, persistedProduct = null, productImage = null }) => [
      {
        id: `product-${visibleProduct.id}`,
        kind: 'product',
        productId: visibleProduct.id,
        productName: visibleProduct.name,
        productIndex: visibleProduct.productIndex,
        productImage,
        persistedProduct,
        productOutOfStock: isTrackedProductOutOfStock(visibleProduct),
      },
      ...visibleProduct.visibleVariants.map((variant) => ({
        id: `variant-${variant.id}`,
        kind: 'variant',
        variantId: variant.id,
        variantName: variant.name,
        productIndex: visibleProduct.productIndex,
        variantIndex: variant.variantIndex,
        persistedVariant: persistedProduct?.variants?.[variant.variantIndex] ?? null,
        pendingUntracked: Boolean(variant.isPendingUntracked),
        variantOutOfStock: isTrackedVariantOutOfStock(variant),
      })),
    ]
  )
}

function canReuseInventoryTableRow(previousRow, nextRow) {
  if (!previousRow || previousRow.kind !== nextRow.kind || previousRow.id !== nextRow.id) {
    return false
  }

  if (nextRow.kind === 'product') {
    return (
      previousRow.productId === nextRow.productId &&
      previousRow.productName === nextRow.productName &&
      previousRow.productIndex === nextRow.productIndex &&
      previousRow.productImage === nextRow.productImage &&
      previousRow.persistedProduct === nextRow.persistedProduct &&
      previousRow.productOutOfStock === nextRow.productOutOfStock
    )
  }

  return (
    previousRow.variantId === nextRow.variantId &&
    previousRow.variantName === nextRow.variantName &&
    previousRow.productIndex === nextRow.productIndex &&
    previousRow.variantIndex === nextRow.variantIndex &&
    previousRow.persistedVariant === nextRow.persistedVariant &&
    previousRow.pendingUntracked === nextRow.pendingUntracked &&
    previousRow.variantOutOfStock === nextRow.variantOutOfStock
  )
}

function inventoryProductCountText(total, productType = defaultInventoryProductType) {
  if (productType === 'add-ons') {
    return `${total} add-on${total === 1 ? '' : 's'}`
  }

  return `${total} product${total === 1 ? '' : 's'}`
}

function InventoryProductTypeFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== defaultInventoryProductType

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label="Filter inventory product type"
        selected={isFiltered}
        className="w-fit min-w-[9rem]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {inventoryProductTypeFilterOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.icon}
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function getInventoryTableRowPresentation(row) {
  if (row.productOutOfStock || row.variantOutOfStock) {
    return { tone: 'destructive' }
  }

  if (row.kind === 'product' || row.pendingUntracked) {
    return { tone: 'muted' }
  }

  return { tone: 'neutral' }
}

function InventoryManagementForm({
  inventoryData,
  meta,
  mutation,
  query,
  filterQuery,
  productType,
  showUntracked,
  onSearchChange,
  onProductTypeChange,
  onShowUntrackedChange,
  inventorySetupPath,
  productCreatePath,
  isFetching = false,
}) {
  const { isReadOnly } = useIsReadOnly()
  const normalizedInventoryData = useMemo(
    () => normalizeInventoryFormValues(inventoryData),
    [inventoryData]
  )
  const normalizedInventorySignature = useMemo(
    () => JSON.stringify(normalizedInventoryData.products),
    [normalizedInventoryData]
  )
  const latestProductSlots = useMemo(
    () => buildInventoryProductSlots(inventoryData),
    [inventoryData]
  )
  const [persistedProducts, setPersistedProducts] = useState(normalizedInventoryData.products)
  const [productSlots, setProductSlots] = useState(latestProductSlots)
  const draftOnHandByVariantIdRef = useRef(new Map())
  const inventoryTableRowCacheRef = useRef(new Map())
  const form = useAppForm({
    defaultValues: normalizedInventoryData,
    // Server-side row validation should not trap the save bar in a disabled state
    // after the florist fixes a quantity and tries again.
    canSubmitWhenInvalid: true,
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          const response = await mutation.mutateAsync(buildInventoryUpdatePayload(value))
          const normalizedResponse = normalizeInventoryFormValues(response?.data ?? [])
          const responseProductSlots = buildInventoryProductSlots(response?.data ?? [])

          toast.success('Inventory saved successfully')
          draftOnHandByVariantIdRef.current.clear()
          setPersistedProducts(normalizedResponse.products)
          setProductSlots(responseProductSlots)
          requestAnimationFrame(() => formApi.reset(normalizedResponse))
        } catch (error) {
          toast.error('Failed to save inventory', {
            description: getErrorMessage(error) || 'Please try again',
          })

          if (error?.response?.data?.errors) {
            return { fields: normalizeInventoryFieldErrors(error.response.data.errors) }
          }
        }
      },
    },
  })
  const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue)
  const inventoryProducts = useStore(form.store, (state) => state.values.products ?? [])
  const appliedInventorySignatureRef = useRef(normalizedInventorySignature)
  const canTrackInventory = Boolean(meta?.can_track_inventory)
  const visibleInventoryProducts = useMemo(
    () =>
      buildVisibleInventoryProducts({
        products: inventoryProducts,
        persistedProducts,
        productSlots,
        query: filterQuery,
        productType,
        showUntracked,
      }),
    [filterQuery, inventoryProducts, persistedProducts, productSlots, productType, showUntracked]
  )
  // Preserve the last committed row objects so unrelated inventory edits do not
  // force every sibling row through TanStack's wrapper churn on each keystroke.
  const previousInventoryTableRowsById = inventoryTableRowCacheRef.current
  /* eslint-disable react-hooks/refs */
  const inventoryTableRows = useMemo(() => {
    return buildInventoryTableRows(visibleInventoryProducts).map((row) => {
      const previousRow = previousInventoryTableRowsById.get(row.id)

      return canReuseInventoryTableRow(previousRow, row) ? previousRow : row
    })
  }, [previousInventoryTableRowsById, visibleInventoryProducts])
  /* eslint-enable react-hooks/refs */
  const onSetProductTrackingState = useCallback(
    (params) => setProductTrackingState({ ...params, draftOnHandByVariantIdRef }),
    []
  )
  const onSetVariantTrackingState = useCallback(
    (params) => setVariantTrackingState({ ...params, draftOnHandByVariantIdRef }),
    []
  )
  const inventoryTableColumns = useMemo(
    () => [
      {
        id: 'product-variant',
        header: 'Product / Variant',
        enableSorting: false,
        cell: ({ row }) => <InventoryProductVariantCell rowData={row.original} form={form} />,
        meta: {
          label: 'Product / Variant',
          kind: 'primary',
          width: '28rem',
          minWidth: '18rem',
        },
      },
      {
        id: 'track',
        header: 'Track',
        enableSorting: false,
        cell: ({ row }) => (
          <InventoryTrackCell
            rowData={row.original}
            form={form}
            canTrackInventory={canTrackInventory}
            onSetProductTrackingState={onSetProductTrackingState}
            onSetVariantTrackingState={onSetVariantTrackingState}
          />
        ),
        meta: {
          label: 'Track',
          kind: 'text',
          width: '14rem',
          minWidth: '11rem',
        },
      },
      {
        id: 'on-hand',
        header: 'On hand',
        enableSorting: false,
        cell: ({ row }) => (
          <InventoryOnHandCell
            rowData={row.original}
            form={form}
            canTrackInventory={canTrackInventory}
          />
        ),
        meta: {
          label: 'On hand',
          kind: 'number',
          align: 'end',
          width: '14rem',
          minWidth: '10rem',
        },
      },
    ],
    [canTrackInventory, form, onSetProductTrackingState, onSetVariantTrackingState]
  )
  const totalProductCount = inventoryProducts.length
  const filteredProductCount = useMemo(
    () =>
      inventoryProducts.filter((product) => inventoryProductMatchesType(product, productType))
        .length,
    [inventoryProducts, productType]
  )

  useEffect(() => {
    if (normalizedInventorySignature === appliedInventorySignatureRef.current || !isDefaultValue) {
      return
    }

    const frameId = requestAnimationFrame(() => {
      appliedInventorySignatureRef.current = normalizedInventorySignature
      draftOnHandByVariantIdRef.current.clear()
      setPersistedProducts(normalizedInventoryData.products)
      setProductSlots(latestProductSlots)
      form.reset(normalizedInventoryData)
    })

    return () => cancelAnimationFrame(frameId)
  }, [
    form,
    isDefaultValue,
    latestProductSlots,
    normalizedInventoryData,
    normalizedInventorySignature,
  ])

  useEffect(() => {
    inventoryTableRowCacheRef.current = new Map(inventoryTableRows.map((row) => [row.id, row]))
  }, [inventoryTableRows])

  return (
    <form.AppForm>
      <form.Form className="space-y-5">
        {!canTrackInventory ? (
          <InfoNotice title="Add a location first" tone="warning" icon={<MapPin />}>
            To track what's on hand here, first create an active location.{` `}
            {inventorySetupPath ? (
              <Typography variant="body" tone="interaction" interaction="link">
                <Link {...inventorySetupPath.link}>Create a location</Link>
              </Typography>
            ) : null}
          </InfoNotice>
        ) : null}

        <div className="space-y-2.5">
          <IndexDataTable
            mode="client"
            data={inventoryTableRows}
            columns={inventoryTableColumns}
            getRowId={(row) => row.id}
            getRowPresentation={getInventoryTableRowPresentation}
            toolbar={
              <IndexTableToolbar
                primary={
                  <IndexTableSearchBox
                    value={query}
                    onCommit={onSearchChange}
                    countText={inventoryProductCountText(
                      visibleInventoryProducts.length,
                      productType
                    )}
                    isFetching={isFetching}
                    placeholder="Search products or variants..."
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                      }
                    }}
                  />
                }
                actions={
                  <>
                    <InventoryProductTypeFilterSelect
                      value={productType}
                      onValueChange={onProductTypeChange}
                    />
                    <ToggleButton
                      pressed={showUntracked}
                      pressedVariant="secondary"
                      size="sm"
                      onClick={() => onShowUntrackedChange(!showUntracked)}
                      className="h-9"
                    >
                      {showUntracked ? <EyeOff /> : <Eye />}
                      {showUntracked ? 'Hide untracked' : 'Show untracked'}
                    </ToggleButton>
                  </>
                }
              />
            }
            emptyState={
              <InventoryEmptyState
                totalProductCount={totalProductCount}
                filteredProductCount={filteredProductCount}
                query={filterQuery}
                productType={productType}
                showUntracked={showUntracked}
                onShowUntrackedChange={onShowUntrackedChange}
                productCreatePath={productCreatePath}
              />
            }
            tableClassName="text-sm"
          />
        </div>

        {!isReadOnly ? <SaveBar mutation={mutation} submitButtonText="Save inventory" /> : null}
      </form.Form>
    </form.AppForm>
  )
}

function InventoryEmptyState({
  totalProductCount,
  filteredProductCount,
  query,
  productType,
  showUntracked,
  onShowUntrackedChange,
  productCreatePath,
}) {
  const { isReadOnly } = useIsReadOnly()
  const filteredTypeLabel = productType === 'add-ons' ? 'add-ons' : 'products'
  const hasProductTypeFilter = productType !== defaultInventoryProductType

  return (
    <div className="px-4 py-8 text-center">
      <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <Boxes className="size-5" />
      </div>
      {query ? (
        <Typography variant="supporting">No products or variants matched your search.</Typography>
      ) : hasProductTypeFilter && filteredProductCount === 0 ? (
        <Typography variant="supporting">
          No {filteredTypeLabel} match this inventory view.
        </Typography>
      ) : totalProductCount === 0 ? (
        <div className="space-y-2">
          <Typography variant="supporting">You haven't created any products yet.</Typography>
          {!isReadOnly && productCreatePath ? (
            <>
              {' '}
              <Typography variant="bodySmall" tone="interaction" interaction="link">
                <Link {...productCreatePath.link}>Create your first product</Link>
              </Typography>
            </>
          ) : null}
        </div>
      ) : !showUntracked ? (
        <div className="space-y-2">
          <Typography variant="supporting">
            You're not tracking inventory on any products right now.
          </Typography>
          <Button variant="link" size="sm" onClick={() => onShowUntrackedChange(true)}>
            Show untracked products
          </Button>
        </div>
      ) : (
        <Typography variant="supporting">Nothing matches this view right now.</Typography>
      )}
    </div>
  )
}

function InventoryProductVariantCell({ rowData, form }) {
  if (rowData.kind === 'product') {
    return <InventoryProductNameCell rowData={rowData} form={form} />
  }

  return <InventoryVariantNameCell rowData={rowData} form={form} />
}

function InventoryTrackCell({
  rowData,
  form,
  canTrackInventory,
  onSetProductTrackingState,
  onSetVariantTrackingState,
}) {
  if (rowData.kind === 'product') {
    return (
      <InventoryProductTrackCell
        rowData={rowData}
        form={form}
        canTrackInventory={canTrackInventory}
        onSetProductTrackingState={onSetProductTrackingState}
      />
    )
  }

  return (
    <InventoryVariantTrackCell
      rowData={rowData}
      form={form}
      canTrackInventory={canTrackInventory}
      onSetVariantTrackingState={onSetVariantTrackingState}
    />
  )
}

function InventoryOnHandCell({ rowData, form, canTrackInventory }) {
  if (rowData.kind === 'product') {
    return <InventoryProductOnHandCell rowData={rowData} form={form} />
  }

  return (
    <InventoryVariantOnHandCell
      rowData={rowData}
      form={form}
      canTrackInventory={canTrackInventory}
    />
  )
}

function InventoryProductNameCell({ rowData, form }) {
  const product = useStore(
    form.store,
    (state) => state.values.products?.[rowData.productIndex] ?? null
  )

  if (!product) {
    return null
  }

  const productOutOfStock = isTrackedProductOutOfStock(product)
  const trackedVariantCount = getTrackedVariantCount(product)
  const totalVariantCount = product.variants?.length ?? 0
  const productName = product.name ?? rowData.productName

  return (
    <div className="flex min-w-0 items-center gap-3">
      <ProductInventoryThumbnail image={rowData.productImage} productName={productName} />
      <div className="min-w-0 space-y-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <LinkToProduct productId={rowData.productId} productName={productName} />
          </div>
          {productOutOfStock ? <Badge variant="warning">All tracked stock is 0</Badge> : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="secondary">{trackedVariantCount} tracked</Badge>
          <Typography variant="label" tone="muted">
            /
          </Typography>
          <Badge variant="muted">{totalVariantCount} variants</Badge>
        </div>
      </div>
    </div>
  )
}

function InventoryProductTrackCell({
  rowData,
  form,
  canTrackInventory,
  onSetProductTrackingState,
}) {
  const { isReadOnly } = useIsReadOnly()
  const product = useStore(
    form.store,
    (state) => state.values.products?.[rowData.productIndex] ?? null
  )

  if (!product) {
    return null
  }

  const trackAllState = getProductTrackAllState(product)
  const trackAllDisabled = isReadOnly || (!canTrackInventory && trackAllState !== true)
  const productName = product.name ?? rowData.productName

  return (
    <div className="space-y-1">
      <div className="flex min-h-10 items-center gap-2">
        <Checkbox
          id={`inventory-track-all-${rowData.productId}`}
          checked={trackAllState}
          onCheckedChange={() =>
            onSetProductTrackingState({
              form,
              productIndex: rowData.productIndex,
              persistedProduct: rowData.persistedProduct,
              shouldTrack: trackAllState === true ? false : canTrackInventory,
            })
          }
          disabled={trackAllDisabled}
          aria-label={`Track all variants for ${productName}`}
        />
        <Label
          htmlFor={`inventory-track-all-${rowData.productId}`}
          tone={trackAllDisabled ? 'muted' : 'default'}
          weight="semibold"
        >
          Track all variants
        </Label>
      </div>
    </div>
  )
}

function InventoryProductOnHandCell({ rowData, form }) {
  const product = useStore(
    form.store,
    (state) => state.values.products?.[rowData.productIndex] ?? null
  )

  if (!product) {
    return null
  }

  const trackedVariantCount = getTrackedVariantCount(product)
  const trackedOnHandTotal = getTrackedProductOnHandTotal(product)
  const productOutOfStock = isTrackedProductOutOfStock(product)

  return (
    <div className="flex min-h-10 items-center justify-end">
      <Typography
        variant="body"
        tone={productOutOfStock ? 'destructive' : 'default'}
        weight="semibold"
        className="block tabular-nums"
      >
        {trackedVariantCount
          ? `${trackedOnHandTotal} unit${trackedOnHandTotal === 1 ? '' : 's'} on hand`
          : 'Not tracking'}
      </Typography>
    </div>
  )
}

function InventoryVariantNameCell({ rowData, form }) {
  const variant = useStore(
    form.store,
    (state) =>
      state.values.products?.[rowData.productIndex]?.variants?.[rowData.variantIndex] ?? null
  )

  if (!variant) {
    return null
  }

  const isOutOfStock = isTrackedVariantOutOfStock(variant)

  return (
    <div className="flex min-h-10 items-center pl-2 @[40rem]/index-table:pl-6">
      <div className="flex min-w-0 items-center gap-2">
        <ChevronRight className="size-4 text-muted-foreground" />
        <Typography
          variant="label"
          className="block min-w-0 line-clamp-2 [overflow-wrap:anywhere] sm:text-base"
          title={variant.name}
        >
          {variant.name}
        </Typography>
        {isOutOfStock ? (
          <Badge variant="warning">
            <TriangleAlert />
            Out of stock
          </Badge>
        ) : null}
      </div>
    </div>
  )
}

function InventoryVariantTrackCell({
  rowData,
  form,
  canTrackInventory,
  onSetVariantTrackingState,
}) {
  const { isReadOnly } = useIsReadOnly()
  const trackFieldName = `products[${rowData.productIndex}].variants[${rowData.variantIndex}].use_inventory`
  const variant = useStore(
    form.store,
    (state) =>
      state.values.products?.[rowData.productIndex]?.variants?.[rowData.variantIndex] ?? null
  )

  if (!variant) {
    return null
  }

  const persistedUsesInventory = Boolean(rowData.persistedVariant?.use_inventory)

  return (
    <form.AppField name={trackFieldName}>
      {(field) => {
        const disabled = isReadOnly || (!canTrackInventory && !persistedUsesInventory)

        return (
          <div className="space-y-1 @[40rem]/index-table:pl-6">
            <div className="flex min-h-10 items-center gap-2">
              <Checkbox
                id={`inventory-track-${rowData.variantId}`}
                checked={Boolean(field.state.value)}
                onCheckedChange={(checked) =>
                  onSetVariantTrackingState({
                    form,
                    productIndex: rowData.productIndex,
                    variantIndex: rowData.variantIndex,
                    variantId: rowData.variantId,
                    persistedVariant: rowData.persistedVariant,
                    shouldTrack: checked === true,
                  })
                }
                disabled={disabled}
              />
              <Label
                htmlFor={`inventory-track-${rowData.variantId}`}
                tone={disabled ? 'muted' : 'default'}
              >
                Track stock
              </Label>
            </div>
            <InlineFieldError errors={field.state.meta.errors} />
          </div>
        )
      }}
    </form.AppField>
  )
}

function InventoryVariantOnHandCell({ rowData, form, canTrackInventory }) {
  const { isReadOnly } = useIsReadOnly()
  const onHandFieldName = `products[${rowData.productIndex}].variants[${rowData.variantIndex}].on_hand`
  const variant = useStore(
    form.store,
    (state) =>
      state.values.products?.[rowData.productIndex]?.variants?.[rowData.variantIndex] ?? null
  )

  if (!variant) {
    return null
  }

  const isPendingUntracked = isPendingUntrackedVariant(variant, rowData.persistedVariant)

  return (
    <form.AppField name={onHandFieldName}>
      {(field) => (
        <div className="space-y-1">
          <div className="flex min-h-10 items-center justify-end">
            {variant.use_inventory ? (
              isReadOnly ? (
                <Typography
                  variant="label"
                  tone={field.state.value === '' ? 'muted' : 'default'}
                  className="block tabular-nums"
                >
                  {field.state.value === '' ? 'Not set' : field.state.value}
                </Typography>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    value={field.state.value ?? ''}
                    onChange={(event) =>
                      field.handleChange(
                        sanitizeInventoryQuantityInput(event.target.value, field.state.value)
                      )
                    }
                    onKeyDown={(event) => {
                      if (['-', '+', 'e', 'E', '.'].includes(event.key)) {
                        event.preventDefault()
                      }
                    }}
                    onBlur={field.handleBlur}
                    disabled={!canTrackInventory}
                    size="sm"
                    aria-invalid={field.state.meta.errors.length > 0 || undefined}
                    className="max-w-24 text-right tabular-nums"
                    aria-label={`${variant.name} quantity on hand`}
                  />
                  <Typography variant="supporting" className="shrink-0">
                    units
                  </Typography>
                </div>
              )
            ) : (
              <Typography variant="supporting" className="block">
                {isPendingUntracked ? 'Stops tracking on save' : 'Not tracked'}
              </Typography>
            )}
          </div>
          <InlineFieldError errors={field.state.meta.errors} />
        </div>
      )}
    </form.AppField>
  )
}

function LinkToProduct({ productId, productName }) {
  const path = usePathHelper()

  return (
    <Link {...path.products.show(productId).link} title={productName}>
      <Typography
        variant="labelStrong"
        weight="semibold"
        interaction="hoverUnderline"
        className="block line-clamp-2 [overflow-wrap:anywhere]"
      >
        {productName}
      </Typography>
    </Link>
  )
}

function ProductInventoryThumbnail({ image, productName }) {
  return (
    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
      {image?.url ? (
        <img
          src={image.url}
          alt={productName}
          width={48}
          height={48}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
      ) : (
        <Boxes className="size-5 text-muted-foreground" aria-hidden="true" />
      )}
    </div>
  )
}

function setProductTrackingState({
  form,
  productIndex,
  persistedProduct,
  shouldTrack,
  draftOnHandByVariantIdRef,
}) {
  const variants = form.getFieldValue(`products[${productIndex}].variants`) ?? []

  variants.forEach((variant, variantIndex) => {
    setVariantTrackingState({
      form,
      productIndex,
      variantIndex,
      variantId: variant.id,
      persistedVariant: persistedProduct?.variants?.[variantIndex] ?? null,
      shouldTrack,
      draftOnHandByVariantIdRef,
    })
  })
}

export function setVariantTrackingState({
  form,
  productIndex,
  variantIndex,
  variantId,
  persistedVariant,
  shouldTrack,
  draftOnHandByVariantIdRef,
}) {
  const trackFieldName = `products[${productIndex}].variants[${variantIndex}].use_inventory`
  const onHandFieldName = `products[${productIndex}].variants[${variantIndex}].on_hand`
  const currentOnHand = form.getFieldValue(onHandFieldName)
  const defaultOnHand = persistedVariant?.on_hand ?? ''
  const defaultUsesInventory = Boolean(persistedVariant?.use_inventory)

  form.setFieldValue(trackFieldName, shouldTrack)

  if (shouldTrack) {
    if (currentOnHand === '' || currentOnHand === null || typeof currentOnHand === 'undefined') {
      const draftOnHand = draftOnHandByVariantIdRef.current.get(variantId)
      const hasSavedOnHand = !(defaultOnHand === '' || defaultOnHand === null)
      const nextOnHand =
        typeof draftOnHand !== 'undefined'
          ? draftOnHand
          : defaultUsesInventory || hasSavedOnHand
            ? defaultOnHand
            : '0'

      form.setFieldValue(onHandFieldName, nextOnHand)
    }

    return
  }

  draftOnHandByVariantIdRef.current.set(variantId, currentOnHand ?? '')

  if (!defaultUsesInventory) {
    form.setFieldValue(onHandFieldName, defaultOnHand)
  }
}

function InlineFieldError({ errors = [] }) {
  if (!errors.length) {
    return null
  }

  return (
    <Typography variant="label" tone="destructive">
      {errors.map((error) => error?.message ?? error?.toString()).join(', ')}
    </Typography>
  )
}
